import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import axios from 'axios';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';
import AlertComment from '../../../classes/AlertComment';
import { AlertCommentType } from '../../../utility/types/AlertCommentType';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';

const javaURI = envConfig.apiUrl;
const findCommenterById = (users, commentUserId) => users.find((user) => user.id === commentUserId);

const selectCommentList = (state) => state.conversation.comments;
const selectUserList = (state) => state.conversation.subscriptionUserList;
const selectIsLoadingConversation = (state) => state.conversation.loading;
const selectDeletedCommentIds = (state) => state.conversation.activeViewDeletedCommentIds;
const selectConversationHasNextPage = (state) => state.conversation.hasNextPage;
const selectConversationTotalNumRecords = (state) => state.conversation.totalNumOfRecords;

const selectUserById = createSelector([
  selectUserList,
  (userList, userId) => userId,
], (userList, userId) => userList.find((user) => user.id === userId));

// skipping because of complexity, just simply maps the comment list
/* istanbul ignore next */
const selectAlertCommentList = createSelector(
  [selectCommentList, selectUserList, selectDeletedCommentIds],
  (comments, users, deletedComments) => {
    if (comments && comments.length) {
      return comments.map((comment) => {
        const commentingUser = findCommenterById(users, comment.userId);
        return (new AlertComment(
          comment.alertId,
          comment.id,
          comment.text,
          comment.timestamp,
          comment.userId,
          commentingUser ? commentingUser.firstName : '',
          commentingUser ? commentingUser.lastName : '',
          deletedComments.includes(comment.id) ? AlertCommentType.DELETED : comment.type,
        ));
      });
    }
    return [];
  },
);

const deleteComment = createAsyncThunk('conversation/deleteComment',
  async (args, { rejectWithValue }) => {
    try {
      const { id } = args;
      await RaaAuthenticatedRemoteService.globalInstance()
        .delete(`${envConfig.apiUrl}/api/v1/alert_comment/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.date,
      });
    }
  });

const addNewComment = createAsyncThunk('conversation/addNewComment',
  async (args, { rejectWithValue, getState }) => {
    try {
      const { alert } = getState().singleAlert;
      const { text, userId } = args;
      const params = new URLSearchParams();
      params.append('text', text);
      params.append('alertId', alert.id);
      const response = await axios.post('/alerts/data/add-alert-comment', params);
      return {
        id: response.data.data,
        alertId: alert.id,
        userId,
        text,
        type: 1,
        timestamp: moment().unix(),
      };
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.date,
      });
    }
  });

const fetchComment = createAsyncThunk(
  'conversation/fetchComment',
  async (args, { rejectWithValue }) => {
    const { id } = args;
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${javaURI}/api/v1/alert_comment/${id}`);
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.date,
      });
    }
  },
);
const fetchComments = createAsyncThunk(
  'conversation/fetchComments',
  async (arg, { rejectWithValue, getState }) => {
    try {
      const { currPage, fetchTimestamp } = getState().conversation;
      const { alert } = getState().singleAlert;
      const queryTimestamp = fetchTimestamp || moment().unix();
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(
          `${javaURI}/api/v1/alert_comment/alert?id=${alert.id}&timestamp=${queryTimestamp}&pageNo=${currPage}&pageSize=${10}`,
        );
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.date,
      });
    }
  },
);
const fetchSubscriptionUserList = createAsyncThunk(
  'conversation/fetchSubscriptionUserList',
  async (arg, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance().get(`${envConfig.apiUrl}/api/v1/user`);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.date,
      });
    }
  },
);
const initialState = {
  comments: [],
  activeViewDeletedCommentIds: [],
  loading: true,
  loadingUsersList: true,
  currPage: 0,
  hasNextPage: true,
  totalNumOfRecords: 0,
  subscriptionUserList: [],
  errors: [],
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  fetchTimestamp: null,
};

const conversationSlice = createSlice({
  name: 'conversation',
  initialState,
  reducers: {
    clearConversationSlice: () => initialState,
    addDeletedCommentId: (state, action) => {
      const { id } = action.payload;
      state.comments = state.comments.map((comment) => {
        if (comment.id === id) {
          return { ...comment, type: 3 };
        }
        return comment;
      });
    },
    updateActiveViewAlertComment: (state, action) => {
      const index = state.comments
        .findIndex((comment) => comment.id === action.payload.commentId);
      if (index !== -1) {
        state.comments[index] = action.payload.comment;
      } else {
        state.comments.push(action.payload.comment);
      }
    },
    setFetchTimestamp: (state, action) => {
      state.fetchTimestamp = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchComments.pending, (state) => {
        state.loading = true;
        state.error = false;
        state.errors = [];
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        const { alertCommentList, pageInfo } = action.payload;
        if (!state.comments.length) {
          state.comments = alertCommentList;
        } else {
          const ids = new Set(state.comments.map((comment) => comment.id));
          state.comments = [
            ...state.comments,
            ...alertCommentList.filter((comment) => !ids.has(comment.id)),
          ];
        }
        state.totalNumOfRecords = pageInfo.totalRecords;
        state.hasNextPage = pageInfo.hasNext;
        state.currPage += 1;
        state.crudStatus = CrudStatus.SUCCESS;
        state.loading = false;
        state.error = false;
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.loading = false;
        state.errors = [action.payload];
        state.crudStatus = CrudStatus.ERROR;
      })
      .addCase(fetchComment.pending, (state) => {
        state.crudStatus = CrudStatus.PENDING;
        state.crudOperation = CrudType.READ;
        state.errors = [];
      })
      .addCase(fetchComment.fulfilled, (state, action) => {
        const { alertComment } = action.payload;
        state.crudStatus = CrudStatus.SUCCESS;
        const latestCommentIndex = state.comments
          .findIndex((comment) => comment.id === alertComment.id);
        if (latestCommentIndex !== -1) {
          if (alertComment.timestamp > state.comments[latestCommentIndex].timestamp) {
            state.comments[latestCommentIndex] = alertComment;
          }
        } else if (latestCommentIndex === -1) {
          state.comments.push(alertComment);
        }
      })
      .addCase(fetchComment.rejected, (state, action) => {
        // state.loading = false;
        state.errors = [action.payload];
        state.crudStatus = CrudStatus.ERROR;
      })
      .addCase(fetchSubscriptionUserList.pending, (state) => {
        state.loadingUserList = true;
      })
      .addCase(fetchSubscriptionUserList.fulfilled, (state, action) => {
        state.subscriptionUserList = action.payload.users;
        state.loadingUserList = false;
      })
      .addCase(fetchSubscriptionUserList.rejected, (state, action) => {
        state.loadingUserList = false;
        state.errors = [action.payload];
      })
      .addCase(addNewComment.pending, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(addNewComment.fulfilled, (state, action) => {
        const commentId = action.payload.id;
        const index = state.comments
          .findIndex((comment) => comment.id === commentId);
        if (index === -1) {
          state.comments.push(action.payload);
        } else {
          state.comments[index] = action.payload;
        }
        state.crudStatus = CrudStatus.SUCCESS;
      })
      .addCase(addNewComment.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = [action.payload];
      })
      .addCase(deleteComment.pending, (state) => {
        state.errors = [];
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { id } = action.payload;
        state.comments = state.comments
          .filter((comment) => comment.alertId !== id);
        state.crudStatus = CrudStatus.SUCCESS;
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = [action.payload];
      });
  },
});

const { actions, reducer } = conversationSlice;
export const {
  setFetchTimestamp,
  addDeletedCommentId,
  updateActiveViewAlertComment,
  clearConversationSlice,
} = actions;
export {
  fetchComment,
  fetchComments,
  fetchSubscriptionUserList,
  selectCommentList,
  selectAlertCommentList,
  selectIsLoadingConversation,
  selectDeletedCommentIds,
  selectConversationHasNextPage,
  selectConversationTotalNumRecords,
  addNewComment,
  deleteComment,
  findCommenterById,
  selectUserList,
  selectUserById,
};
export default reducer;
